import { useConfig } from '../config';
import { useRef } from 'react';

type Criteo = {
	push: (events: CriteoEvent[]) => void;
};

function getCriteo() {
	if (typeof window !== 'undefined') {
		return (window as any).criteo_q as Criteo;
	}
}

export function useCriteo() {
	const { criteoId } = useConfig();
	const calledRef = useRef(false);
	const keyRef = useRef<string | null>(null);

	let deviceType: 't' | 'm' | 'd' = 'd';
	if (typeof navigator !== 'undefined') {
		if (navigator.userAgent) {
			deviceType = /iPad/.test(navigator.userAgent)
				? 't'
				: /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent)
					? 'm'
					: 'd';
		}
	}

	const setAccountEvent: SetAccountEvent = { event: 'setAccount', account: +criteoId };
	const setSiteTypeEvent: SetSiteTypeEvent = { event: 'setSiteType', type: deviceType };

	function sendCriteoEventOnce(criteoEvent: CriteoEvent, key?: string) {
		const shouldRun = key ? keyRef.current !== key : !calledRef.current;
		if (shouldRun) {
			if (key) keyRef.current = key;
			calledRef.current = true;

			sendCriteoEvent(criteoEvent);
		}
	}

	function sendCriteoEvent(criteoEvent: CriteoEvent) {
		const criteo = getCriteo();
		if (!criteo || !criteoId) return;
		const criteoEvents = [setAccountEvent, setSiteTypeEvent, criteoEvent];

		criteo.push(criteoEvents);
	}

	return { sendCriteoEvent, sendCriteoEventOnce, criteoId };
}

interface CriteoCommon {
	event: string;
}

type CriteoEvent =
	| SetAccountEvent
	| SetSiteTypeEvent
	| ViewHomeEvent
	| ViewListEvent
	| TrackTransactionEvent
	| ViewItemEvent
	| ViewBasketEvent
	| AddToCartEvent;

interface SetAccountEvent extends CriteoCommon {
	event: 'setAccount';
	account: number;
}

interface SetSiteTypeEvent extends CriteoCommon {
	event: 'setSiteType';
	type: 't' | 'm' | 'd';
}

interface ViewHomeEvent extends CriteoCommon {
	event: 'viewHome';
	tms: string;
}

interface ViewListEvent extends CriteoCommon {
	event: 'viewList';
	item: number[];
}

interface TrackTransactionEvent extends CriteoCommon {
	event: 'trackTransaction';
	id: string;
	item: {
		id: number;
		quantity: number;
		price: number;
	}[];
}

interface ViewItemEvent extends CriteoCommon {
	event: 'viewItem';
	item: number;
}

interface ViewBasketEvent extends CriteoCommon {
	event: 'viewBasket';
	item: {
		id: string;
		name: string;
		quantity: number;
		price: string;
	}[];
}

interface AddToCartEvent extends CriteoCommon {
	event: 'addToCart';
	item: {
		id: string;
		price: number;
		quantity: number;
	}[];
}
